import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import { useNavigate } from "react-router-dom";
import { dt } from "../../../../shared/themes/redesignTokens";
import RedesignStatusBadge from "../../../../shared/components/redesign/RedesignStatusBadge";
import ConfirmationDialog from "../../../../shared/components/confirmation/ConfirmationDialog";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import { PriorityTypeCode, ServiceRequestStatusCode, ServiceRequest } from "../../models/models";
import {
  deleteServiceRequest,
  unpublishServiceRequest,
  archiveServiceRequest,
  unarchiveServiceRequest,
} from "../../customerServicingSlice";
import serviceRequestsApi from "../../api/serviceRequestsApi";
import RfsShareButtons from "./RfsShareButtons";
import dayjs from "dayjs";
import PermissionGuard from "../../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";

interface RfsHeaderStripProps {
  sr: PublicServiceRequest;
  preparingCount: number;
  isOwner?: boolean;
  statusCode?: number;
  serviceRequestId?: string;
  onDataRefresh?: () => void;
}

const priorityBadgeConfigs: Record<number, { bg: string; text: string; border: string }> = {
  [PriorityTypeCode.urgent]: { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
  [PriorityTypeCode.high]: { bg: "#FFF7ED", text: "#EA580C", border: "#FED7AA" },
};

const RfsHeaderStrip = ({
  sr,
  preparingCount,
  isOwner = false,
  statusCode,
  serviceRequestId,
  onDataRefresh,
}: RfsHeaderStripProps) => {
  const { translate } = useTranslation(["serviceBoard", "servicing", "serviceOffers", "common"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Actions menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);

  // Determine effective status code for action visibility
  const effectiveStatus = statusCode !== undefined ? statusCode : sr.statusCode;
  const effectiveId = serviceRequestId || sr.id;

  // Action visibility helpers
  const canEdit =
    effectiveStatus === ServiceRequestStatusCode.draft ||
    effectiveStatus === ServiceRequestStatusCode.openForOffers;
  const canDownloadPdf = effectiveStatus !== ServiceRequestStatusCode.draft;
  const canUnpublish =
    (effectiveStatus === ServiceRequestStatusCode.openForOffers ||
      effectiveStatus === ServiceRequestStatusCode.negotiating);
  const canDelete = effectiveStatus === ServiceRequestStatusCode.draft;
  const canArchive = effectiveStatus === ServiceRequestStatusCode.completed;
  const canUnarchive = effectiveStatus === ServiceRequestStatusCode.archived;

  const hasAnyAction =
    isOwner && (canEdit || canDownloadPdf || canUnpublish || canDelete || canArchive || canUnarchive);

  // Action handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/service-requests/edit/${effectiveId}`);
  };

  const handleDownloadPdf = () => {
    handleMenuClose();
    // Build a minimal ServiceRequest-shaped object for the PDF download API
    const srForPdf = {
      id: effectiveId,
      internalNumber: sr.internalNumber,
    } as ServiceRequest;
    serviceRequestsApi.downloadServiceRequestPdf(srForPdf);
  };

  const handleUnpublishClick = () => {
    handleMenuClose();
    setUnpublishConfirmOpen(true);
  };

  const handleUnpublishConfirm = () => {
    setUnpublishConfirmOpen(false);
    dispatch(unpublishServiceRequest(effectiveId)).then(() => {
      navigate("/service-requests");
    });
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    dispatch(deleteServiceRequest(effectiveId)).then(() => {
      navigate("/service-requests");
    });
  };

  const handleArchive = () => {
    handleMenuClose();
    dispatch(archiveServiceRequest(effectiveId)).then(() => {
      if (onDataRefresh) onDataRefresh();
    });
  };

  const handleUnarchive = () => {
    handleMenuClose();
    dispatch(unarchiveServiceRequest(effectiveId)).then(() => {
      if (onDataRefresh) onDataRefresh();
    });
  };

  // Expiration countdown logic
  const getExpirationInfo = () => {
    if (sr.flexibleDates) {
      return { text: translate("servicing:rfsDetail.flexibleTimeline"), color: "#27AE60" };
    }

    const targetDate = sr.serviceEndDate || sr.serviceStartDate;
    if (!targetDate) return null;

    const target = dayjs(targetDate);
    const now = dayjs();
    const diffDays = target.diff(now, "day");
    const diffHours = target.diff(now, "hour") % 24;

    if (diffDays < 0) {
      return { text: translate("serviceBoard:expired"), color: "#DC2626" };
    }

    let timeText: string;
    if (diffDays > 0) {
      timeText = `${diffDays}d ${diffHours > 0 ? `${diffHours}h` : ""}`.trim();
    } else {
      timeText = `${diffHours}h`;
    }

    let color: string;
    if (diffDays > 7) color = "#6B7280";
    else if (diffDays >= 3) color = "#EA580C";
    else color = "#DC2626";

    return {
      text: translate("servicing:rfsDetail.serviceNeededIn", { time: timeText }),
      color,
    };
  };

  const expirationInfo = getExpirationInfo();
  const postedDate = sr.openDate
    ? dayjs(sr.openDate).format("MMM D, YYYY")
    : dayjs(sr.createdDate).format("MMM D, YYYY");

  // Shared menu item styles
  const menuItemSx = {
    fontFamily: dt.font,
    fontSize: "14px",
    fontWeight: 500,
    color: dt.textPrimary,
    gap: 1.5,
    py: 1,
    px: 2,
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: dt.blueSoft },
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      color: dt.textSecondary,
    },
  };

  const deleteMenuItemSx = {
    ...menuItemSx,
    color: "#DC2626",
    "&:hover": { backgroundColor: "#FEF2F2" },
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      color: "#DC2626",
    },
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {/* RFS Number + Posted date + Share buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "13px",
              fontWeight: 700,
              color: dt.blue,
              letterSpacing: "0.3px",
            }}
          >
            {sr.internalNumber}
          </Box>
          <Box sx={{ fontSize: "13px", fontFamily: dt.fontMono, fontWeight: 400, color: dt.textMuted }}>
            |
          </Box>
          <Box
            sx={{
              fontSize: "13px",
              fontFamily: dt.fontMono,
              fontWeight: 400,
              color: dt.textSecondary,
            }}
          >
            {translate("servicing:posted")} {postedDate}
          </Box>

          {/* Share buttons + Owner actions — subtle, secondary */}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
            <RfsShareButtons internalNumber={sr.internalNumber} issue={sr.issue} />

            {/* Owner actions kebab menu */}
            {hasAnyAction && (
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                sx={{
                  color: dt.textSecondary,
                  borderRadius: dt.radiusSm,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: dt.blueGlow,
                    color: dt.blue,
                  },
                }}
              >
                <MoreVertIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Status badges + offers + countdown — all on one row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {/* Urgent / High Priority badge */}
          {sr.priorityTypeCode === PriorityTypeCode.urgent && (
            <RedesignStatusBadge
              label={translate("serviceBoard:urgent")}
              colorConfig={priorityBadgeConfigs[PriorityTypeCode.urgent]}
            />
          )}
          {sr.priorityTypeCode === PriorityTypeCode.high && (
            <RedesignStatusBadge
              label={translate("serviceBoard:priorityHigh")}
              colorConfig={priorityBadgeConfigs[PriorityTypeCode.high]}
            />
          )}

          {/* Open for Offers */}
          <RedesignStatusBadge
            label={translate("serviceBoard:openForOffers")}
            colorConfig={{ bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" }}
          />

          {/* Direct Request */}
          {sr.isDirectToMe && (
            <RedesignStatusBadge
              label={translate("servicing:badge.direct")}
              colorConfig={{ bg: "#E8F8F5", text: "#27AE60", border: "#A3E4D7" }}
            />
          )}

          {/* Offers count — inline with badges */}
          {sr.offersCount > 0 && (
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "12px",
                fontWeight: 600,
                color: dt.textSecondary,
                backgroundColor: "#F3F4F6",
                borderRadius: "12px",
                padding: "3px 10px",
              }}
            >
              {translate("serviceBoard:offersCount", { count: sr.offersCount })}
            </Box>
          )}

          {/* Preparing indicator — inline with badges */}
          {preparingCount > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontFamily: dt.fontMono,
                fontSize: "12px",
                fontWeight: 600,
                color: "#D97706",
                backgroundColor: "#FFF7ED",
                borderRadius: "12px",
                padding: "3px 10px",
                animation: "presencePulse 2s ease-in-out infinite",
                "@keyframes presencePulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.7 },
                },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "#27AE60",
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.3)" },
                  },
                }}
              />
              {translate("serviceOffers:preparingOffers", { count: preparingCount })}
            </Box>
          )}

          {/* Expiration countdown — inline with badges */}
          {expirationInfo && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontFamily: dt.fontMono,
                fontSize: "12px",
                fontWeight: 600,
                color: expirationInfo.color,
                backgroundColor: expirationInfo.color === "#27AE60" ? "#E8F8F5" : expirationInfo.color === "#DC2626" ? "#FEF2F2" : expirationInfo.color === "#EA580C" ? "#FFF7ED" : "#F3F4F6",
                borderRadius: "12px",
                padding: "3px 10px",
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 14 }} />
              {expirationInfo.text}
            </Box>
          )}
        </Box>
      </Box>

      {/* Owner Actions Menu */}
      {hasAnyAction && (
        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                border: `1px solid ${dt.cardBorder}`,
                mt: 0.5,
                minWidth: 180,
                fontFamily: dt.font,
                "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
              },
            },
          }}
        >
          {canEdit && (
            <MenuItem onClick={handleEdit} sx={menuItemSx}>
              <ListItemIcon><EditOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>{translate("servicing:rfsDetail.actions.edit")}</ListItemText>
            </MenuItem>
          )}
          {canDownloadPdf && (
            <MenuItem onClick={handleDownloadPdf} sx={menuItemSx}>
              <ListItemIcon><PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>{translate("servicing:rfsDetail.actions.downloadPdf")}</ListItemText>
            </MenuItem>
          )}
          {canUnpublish && (
            <MenuItem onClick={handleUnpublishClick} sx={menuItemSx}>
              <ListItemIcon><VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>{translate("servicing:rfsDetail.actions.unpublish")}</ListItemText>
            </MenuItem>
          )}
          {canArchive && (
            <MenuItem onClick={handleArchive} sx={menuItemSx}>
              <ListItemIcon><ArchiveOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>{translate("servicing:rfsDetail.actions.archive")}</ListItemText>
            </MenuItem>
          )}
          {canUnarchive && (
            <MenuItem onClick={handleUnarchive} sx={menuItemSx}>
              <ListItemIcon><UnarchiveOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>{translate("servicing:rfsDetail.actions.unarchive")}</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <PermissionGuard module={Modules.Servicing} level={PermissionLevel.Admin}>
              <MenuItem onClick={handleDeleteClick} sx={deleteMenuItemSx}>
                <ListItemIcon><DeleteOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                <ListItemText>{translate("servicing:rfsDetail.actions.delete")}</ListItemText>
              </MenuItem>
            </PermissionGuard>
          )}
        </Menu>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        title={translate("servicing:rfsDetail.actions.deleteConfirmTitle")}
        text={translate("servicing:rfsDetail.actions.deleteConfirmText")}
        confirmButton={translate("servicing:rfsDetail.actions.deleteConfirmButton")}
        closeButton={translate("common:buttons.cancel")}
        severity="error"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteConfirmOpen(false)}
      />

      {/* Unpublish Confirmation Dialog */}
      <ConfirmationDialog
        open={unpublishConfirmOpen}
        title={translate("servicing:rfsDetail.actions.unpublishConfirmTitle")}
        text={translate("servicing:rfsDetail.actions.unpublishConfirmText")}
        confirmButton={translate("servicing:rfsDetail.actions.unpublishConfirmButton")}
        closeButton={translate("common:buttons.cancel")}
        severity="neutral"
        onConfirm={handleUnpublishConfirm}
        onClose={() => setUnpublishConfirmOpen(false)}
      />
    </>
  );
};

export default RfsHeaderStrip;
